import { SortOrderType } from '@repo/common';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { ArrowDown } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTableHeader } from '@components/ui/data.table';

import { DEFAULT_PAGE_LIMIT, DEFAULT_PAGE_NUMBER } from '@/constants';
import { TProductV2 } from '@/features/slice/products/product/product.schema';
import { useUrlFilter } from '@/hooks';
import { formatCurrencyInBDT } from '@/utils';

import { EditProductForm } from '../edit-product';

const ProductNo = ({ count }: { count: number }) => {
    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page') || DEFAULT_PAGE_NUMBER);
    const limit = Number(searchParams.get('limit') || DEFAULT_PAGE_LIMIT);

    // Offset calculation
    const offset = (page - 1) * limit;
    const index = offset + count;

    return <span>{index}</span>;
};

const ProductName = ({ row }: CellContext<TProductV2, unknown>) => {
    const [open, setOpen] = useState(false);
    return (
        <Tooltip>
            <TooltipTrigger>
                <div className="text-left font-medium">
                    {row.original.full_product_name}
                    <Dialog onOpenChange={setOpen} open={open}>
                        <DialogTrigger>
                            <Button
                                className="ml-2"
                                type={'button'}
                                size={'icon'}
                                variant={'outline'}
                            >
                                Edit
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <EditProductForm
                                onSuccessCallback={() => setOpen(false)}
                                productVariationId={row.original.id}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <p className="text-sm font-medium">
                    {row.original.full_product_name}
                </p>
                <p>{row.original.brand_name}</p>
                <p>{row.original.generic_name}</p>
            </TooltipContent>
        </Tooltip>
    );
};

const ProductNameHeader = () => {
    const { setSortBy, setSortOrder, sortOrder } = useUrlFilter();

    const handleSortClick = () => {
        setSortBy('name');
        setSortOrder(
            sortOrder === SortOrderType.ASC
                ? SortOrderType.DESC
                : SortOrderType.ASC,
        );
    };

    return (
        <div className="text-left" onClick={handleSortClick}>
            <span>Name</span>
            <ArrowDown className="ml-1 inline-block h-4 w-4" />
        </div>
    );
};

export const productListColumns: ColumnDef<TProductV2>[] = [
    {
        accessorKey: 'No',
        header: () => {
            return <>No.</>;
        },
        cell: ({ row }) => <ProductNo count={row.index + 1} />,
    },

    {
        accessorKey: 'name',
        header: ProductNameHeader,
        cell: ProductName,
    },

    {
        accessorKey: 'brand_name',
        header: 'Brand',
        cell: ({ row }) => {
            const brandName = row.original.brand_name;
            const firstWord = brandName?.split(' ')[0] || '';
            return (
                <div
                    title={row.getValue('brand')}
                    className="w-[200px] cursor-pointer truncate"
                >
                    <Tooltip>
                        <TooltipTrigger>{firstWord}</TooltipTrigger>
                        <TooltipContent>
                            <p>{brandName}</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            );
        },
    },

    {
        accessorKey: 'purchase_price',
        header: () => <DataTableHeader align="right">Purchase</DataTableHeader>,
        cell: ({ row }) => (
            <div className={'text-right'}>
                {formatCurrencyInBDT(row.original.purchase_price)}
            </div>
        ),
    },

    {
        accessorKey: 'name',
        header: () => (
            <DataTableHeader align="right">Wholesale</DataTableHeader>
        ),
        cell: ({ row }) => (
            <div className={'text-right'}>
                {formatCurrencyInBDT(row.original.wholesale_price)}
            </div>
        ),
    },

    {
        accessorKey: 'name',
        header: () => <DataTableHeader align="right">Retail</DataTableHeader>,
        cell: ({ row }) => (
            <div className={'text-right'}>
                {formatCurrencyInBDT(row.original.retail_price)}
            </div>
        ),
    },

    // {
    //     accessorKey: 'actions',
    //     header: 'Actions',
    //     cell: ({ row }) => <div>{row.original.retail_price}</div>,
    // },
];
